import React, { useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import AddRefundRates from "../../../components/AddRefundRates";
import {
  Grid,
  Link,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { spacing } from "@mui/system";
import Notification from "../../../components/Notification";
import ConfirmDialog from "../../../components/ConfirmDialog";
import axios from "axios";
import ViewRefundRatesDialog from "../../../components/ViewRefundRatesDialog";
import DataGridExport from "../../../components/DataGridExport";
import { usePermissions } from "../../../hooks/usePermissions";
import { CAPABILITIES } from "../../../auth/permissions";

// Helper function to get user's locale decimal separator
const getDecimalSeparator = () => {
  try {
    // Use Intl.NumberFormat to detect the decimal separator for the user's locale
    const numberFormat = new Intl.NumberFormat(navigator.language || "en-US");
    const parts = numberFormat.formatToParts(1.5);
    const decimalPart = parts.find((part) => part.type === "decimal");
    return decimalPart ? decimalPart.value : ".";
  } catch (error) {
    // Fallback to dot if detection fails
    return ".";
  }
};

// Helper function to format value with 2 decimals using user's locale separator
const formatWithTwoDecimals = (value) => {
  if (!value && value !== 0) {
    const separator = getDecimalSeparator();
    return `0${separator}00`;
  }
  const numValue = typeof value === "string" ? parseFloat(String(value).replace(",", ".")) : value;
  const formatted = numValue.toFixed(2);
  const separator = getDecimalSeparator();
  // Replace dot with user's locale separator
  return separator === "," ? formatted.replace(".", ",") : formatted;
};


const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const RefundRatesDataGrid = () => {
  const { hasPermission } = usePermissions();
  const canWriteRefundRates = hasPermission(CAPABILITIES.refundRatesWrite, "W");
  const [tableData, setTableData] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [viewRefundRatesDialog, setViewRefundRatesDialog] = useState({
    isOpen: false,
    title: "",
    countryId: null,
    productId: null,
    countryCode: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: null,
  });

  const fetchRefundRates = useCallback(async () => {
    try {
      const response = await axios.get("/api/v1/refundrates");
      const rates = response.data || [];
      
      // Group by country and productId
      const groupedByCountryAndProduct = rates.reduce((acc, rate) => {
        const key = `${rate.countryId}_${rate.productId}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(rate);
        return acc;
      }, {});

      // Create table data with one row per country/productId combination
      const tableRows = Object.keys(groupedByCountryAndProduct).map((key) => {
        const countryRates = groupedByCountryAndProduct[key];
        const [countryId, productId] = key.split('_');
        const country = countryData.find((c) => c.countryId === parseInt(countryId));
        // Use the first rate's MonthFee and YearFee (assuming they're the same for all rates in a country/product)
        const firstRate = countryRates[0];
        
        return {
          id: key,
          countryId: parseInt(countryId),
          productId: parseInt(productId),
          countryCode: country ? country.countryCode : "",
          rates: countryRates,
        };
      });

      setTableData(tableRows);
    } catch (error) {
      console.error("Error fetching refund rates:", error);
      setNotify({
        isOpen: true,
        message: "Error fetching refund rates",
        type: "error",
      });
    }
  }, [countryData]);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await axios.get("/api/v1/allCountries");
      setCountryData(response.data || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  }, []);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (countryData.length > 0) {
      fetchRefundRates();
    }
  }, [countryData, fetchRefundRates]);

  const columns = [
    { field: "id", headerName: "ID", width: 50, hide: true },
    {
      field: "countryCode",
      headerName: "Country Code",
      flex: 1,
    },
    {
      field: "productId",
      headerName: "Product",
      flex: 1,
      valueGetter: (params) => {
        return params.value === 1 ? "VAT" : params.value === 2 ? "Excise" : "";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => {
        const handleCopyToClipboard = async () => {
          try {
            // Fetch the rates for this country/product
            const response = await axios.get(`/api/v1/refundrates/country/${params.row.countryId}`);
            const allRates = response.data || [];
            const filteredRates = allRates.filter(rate => rate.productId === params.row.productId);
            
            // Sort by fromRate ascending
            const sortedRates = [...filteredRates].sort((a, b) => {
              const fromRateA = parseFloat(String(a.fromRate).replace(",", ".")) || 0;
              const fromRateB = parseFloat(String(b.fromRate).replace(",", ".")) || 0;
              return fromRateA - fromRateB;
            });
            
            // Format rates for Excel (tab-separated, with € symbol and proper formatting)
            const formatForExcel = (value) => {
              const num = parseFloat(String(value).replace(",", "."));
              const parts = num.toFixed(2).split(".");
              const integerPart = parts[0];
              
              // Add thousand separators (dots)
              const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
              
              // Format as € X.XXX,XX (European format)
              return `€ ${formattedInteger},${parts[1]}`;
            };
            
            const formattedRates = sortedRates.map(rate => {
              return `${formatForExcel(rate.fromRate)}\t${formatForExcel(rate.toRate)}\t${formatForExcel(rate.monthFee || 0)}`;
            });
            
            const clipboardText = formattedRates.join("\n");
            await navigator.clipboard.writeText(clipboardText);
            
            setNotify({
              isOpen: true,
              message: `${sortedRates.length} rate(s) copied to clipboard`,
              type: "success",
            });
          } catch (error) {
            console.error("Error copying to clipboard:", error);
            setNotify({
              isOpen: true,
              message: "Error copying to clipboard",
              type: "error",
            });
          }
        };

        const handleDeleteAllRates = async (countryId, productId) => {
          try {
            // Fetch all rates for this country/product
            const response = await axios.get(`/api/v1/refundrates/country/${countryId}`);
            const allRates = response.data || [];
            const filteredRates = allRates.filter(rate => rate.productId === productId);
            
            if (filteredRates.length === 0) {
              setNotify({
                isOpen: true,
                message: "No rates found to delete",
                type: "info",
              });
              return;
            }
            
            // Delete all rates
            const deletePromises = filteredRates.map(rate => 
              axios.delete(`/api/v1/refundrates/${rate.id}`)
            );
            
            await Promise.all(deletePromises);
            
            setNotify({
              isOpen: true,
              message: `${filteredRates.length} rate(s) deleted successfully`,
              type: "success",
            });
            
            fetchRefundRates();
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          } catch (error) {
            console.error("Error deleting rates:", error);
            setNotify({
              isOpen: true,
              message: "Error deleting rates",
              type: "error",
            });
            setConfirmDialog({ ...confirmDialog, isOpen: false });
          }
        };

        return (
          <Box>
            <IconButton
              onClick={() => {
                const country = countryData.find((c) => c.countryId === params.row.countryId);
                setViewRefundRatesDialog({
                  isOpen: true,
                  title: `View Refund Rates - ${country ? country.countryCode : ""} (${params.row.productId === 1 ? "VAT" : "Excise"})`,
                  countryId: params.row.countryId,
                  productId: params.row.productId,
                  countryCode: country ? country.countryCode : "",
                });
              }}
              size="small"
              title="View rates"
            >
              <VisibilityIcon />
            </IconButton>
            <IconButton
              onClick={handleCopyToClipboard}
              size="small"
              title="Copy rates to clipboard"
            >
              <ContentCopyIcon />
            </IconButton>
            {canWriteRefundRates && (
            <IconButton
              onClick={() => {
                const country = countryData.find((c) => c.countryId === params.row.countryId);
                setConfirmDialog({
                  isOpen: true,
                  title: "Delete All Refund Rates",
                  subTitle: `Are you sure you want to delete all refund rates for ${country ? country.countryCode : ""} (${params.row.productId === 1 ? "VAT" : "Excise"})?`,
                  onConfirm: () => handleDeleteAllRates(params.row.countryId, params.row.productId),
                });
              }}
              size="small"
              title="Delete all rates"
              color="error"
              aria-label="Delete all refund rates"
            >
              <DeleteIcon />
            </IconButton>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Helmet title="Refund Rates" />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Refund Rates
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/settings/globalsettings/refundrates">
              Sales Settings
            </Link>
            <Typography>Refund Rates</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />
      <Grid item xs={12} md={6}></Grid>
      <Card mb={0}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 0,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Refund Rates Table
          </Typography>
          <Typography variant="body2" gutterBottom>
            In this table you can search and view Refund Rates per country. You can use the below options to customize the grid.
          </Typography>
        </CardContent>
        <Grid container spacing={0} mt={0}>
          {canWriteRefundRates && (
            <AddRefundRates refreshTable={fetchRefundRates} countryData={countryData} setNotify={setNotify} />
          )}
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
          <ViewRefundRatesDialog
            viewRefundRatesDialog={viewRefundRatesDialog}
            setViewRefundRatesDialog={setViewRefundRatesDialog}
            refreshTable={fetchRefundRates}
            countryData={countryData}
          />
          <Grid item xs={12}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, mb: 2, pr: 2 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <DataGridExport tableData={tableData} columns={columns} />
              </Box>
            </Box>
            <DataGrid
              getRowId={(row) => row.id}
              disableSelectionOnClick
              components={{ Toolbar: GridToolbar }}
              density="compact"
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: false },
                  printOptions: { disableToolbarButton: false },
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
                rowsContainer: {
                  onClick: (e) => {
                    if (e.target.closest("button") || e.target.closest("a")) {
                      e.stopPropagation();
                    }
                  },
                },
              }}
              rows={tableData}
              columns={columns}
              pageSize={15}
              sx={{
                height: 667,
                width: "100%",
                borderRadius: 1,
                backgroundColor: "background.paper",
                boxShadow: 2,
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                marginTop: 0,
              }}
            />
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default RefundRatesDataGrid;

